import { useLayoutEffect, useRef } from 'react';

/**
 * Global modal stacking system.
 *
 * Every popup in the portal (the shared <Modal> component AND any custom
 * fixed-position overlay) must register with this manager so that the most
 * recently opened popup ALWAYS paints above every currently open popup,
 * regardless of DOM order or parent stacking contexts.
 *
 * Each registered layer receives a monotonically increasing z-index above
 * the shared modal base (`--z-modal`, 9000). Because ids are never reused,
 * a layer opened later is guaranteed a higher z-index than one opened
 * earlier — unlimited nesting just works.
 */
export const MODAL_BASE_Z = 9000;
/** Increment per open layer — keeps every layer distinct. */
const Z_STEP = 10;

const activeLayers: number[] = [];
let nextId = 0;

/** Register a new popup layer. Returns a stable unique layer id. */
export function acquireModalLayer(): number {
  const id = nextId++;
  activeLayers.push(id);
  return id;
}

/** Unregister a popup layer (id from acquireModalLayer). */
export function releaseModalLayer(id: number): void {
  const i = activeLayers.indexOf(id);
  if (i >= 0) activeLayers.splice(i, 1);
}

/** True when the given layer is the topmost open popup. */
export function isTopModalLayer(id: number): boolean {
  return activeLayers.length > 0 && activeLayers[activeLayers.length - 1] === id;
}

/**
 * True when any layer opened AFTER `id` is still registered (i.e. a popup is
 * still open above it). Used during unmount cleanup — after the closing layer
 * has already released itself — to decide whether focus may be handed back.
 */
export function isAnyLayerAbove(id: number): boolean {
  for (const layer of activeLayers) {
    if (layer > id) return true;
  }
  return false;
}

/** z-index assigned to a layer. Always above the base and above older layers. */
export function modalZIndex(id: number): number {
  return MODAL_BASE_Z + (id + 1) * Z_STEP;
}

/**
 * Hook for custom overlays (fixed-position divs that do not use <Modal>).
 *
 *   const z = useModalLayer(showDialog);
 *   ...
 *   {showDialog && (
 *     <div className="fixed inset-0 ..." style={{ zIndex: z }}>
 *
 * Pass `active` = the condition that controls whether the overlay is open.
 * The z-index is assigned before paint (useLayoutEffect) so the overlay
 * never flashes below page content. Returns 0 while inactive.
 *
 * Optionally pass `onEscape` — an Escape-key handler that only fires while
 * THIS overlay is the topmost popup (so nested popups close one at a time).
 */
export function useModalLayer(active = true, onEscape?: () => void): number {
  const idRef = useRef<number | null>(null);
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  if (active && idRef.current === null) {
    idRef.current = acquireModalLayer();
  } else if (!active && idRef.current !== null) {
    releaseModalLayer(idRef.current);
    idRef.current = null;
  }

  const z = active && idRef.current !== null ? modalZIndex(idRef.current) : 0;

  useLayoutEffect(() => {
    if (!active) return;
    if (idRef.current === null) {
      idRef.current = acquireModalLayer();
    }
    const id = idRef.current;

    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!isTopModalLayer(id)) return;
      e.stopPropagation();
      onEscapeRef.current?.();
    };
    if (onEscapeRef.current) {
      document.addEventListener('keydown', handler);
    }

    return () => {
      if (onEscapeRef.current) {
        document.removeEventListener('keydown', handler);
      }
      if (idRef.current !== null) {
        releaseModalLayer(idRef.current);
        idRef.current = null;
      }
    };
  }, [active]);

  return z;
}
